import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { PageRecommendationData } from '@post-office/growth-common/confluence-experiments-context';

import { expVal } from '@confluence/feature-experiments';
import { transformEndOfPageRecContent } from '@confluence/page-recommendations/entry-points/transformers';
import { EndOfPageRecommendationQuery } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationQuery.graphql';
import {
	EndOfPageRecommendationData,
	EndOfPageRecommendationQueryVariables,
} from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationQuery';
import {
	type GlobalPageCard,
	type SpaceHoverPageCardQueryData,
	type SpaceHoverPageCardQueryVars,
	type AISnippetQueryData,
	type AISnippetQueryVariables,
	AISnippetQuery,
} from '@confluence/page-card';
import {
	ConfluenceContentType,
	KnowledgeGraphObjectType,
} from '@confluence/page-card/src/Items/AISnippet/__types__/AISnippetQuery';
import { SpaceHoverPageCardQuery } from '@confluence/page-card/src/SpaceHoverPageCardQuery.graphql';

import { getPageDataFromUrl } from './getPageDataFromUrl';

export const usePageRecommendationData = (skip: boolean = false) => {
	const { contentId, spaceKey } = getPageDataFromUrl(globalThis.location.href);

	// Get recommendation data
	const {
		data: recPageData,
		loading: recPageLoading,
		error: recPageError,
	} = useQuery<EndOfPageRecommendationData, EndOfPageRecommendationQueryVariables>(
		EndOfPageRecommendationQuery,
		{
			variables: {
				type: 'page',
				id: contentId ?? '',
				exp: 'end_of_page_v5',
				key: spaceKey ?? '',
				keyString: spaceKey ?? '',
			},
			skip: !contentId || !spaceKey || skip,
		},
	);

	// Transform/sort data and grab first rec
	const recommendedPage: GlobalPageCard | undefined = useMemo(() => {
		const rankedPages: GlobalPageCard[] | undefined = transformAndOrderRecs(
			recPageData?.getRecommendedPages?.recommendedPages,
		);
		return rankedPages?.[0];
	}, [recPageData]);

	// Get avatar and name of the page owner
	const {
		data: hoverPageData,
		loading: hoverPageLoading,
		error: hoverPageError,
	} = useQuery<SpaceHoverPageCardQueryData, SpaceHoverPageCardQueryVars>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpaceHoverPageCardQuery,
		{
			variables: {
				id: recommendedPage ? recommendedPage.id : '',
			},
			skip: !recommendedPage || skip,
		},
	);

	// Get AI Snippet for page. This data is optional to display the message so we won't stop message rednering if it fails.
	const objectType =
		KnowledgeGraphObjectType[expVal('consumption_ai_page_tree_snippets_2', 'type', 'snippet_v1')] ||
		KnowledgeGraphObjectType.snippet_v1;

	const { data: AISnippetData } = useQuery<AISnippetQueryData, AISnippetQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		AISnippetQuery,
		{
			variables: {
				contentId: recommendedPage?.id || '',
				contentType:
					recommendedPage?.type === 'page'
						? ConfluenceContentType.PAGE
						: ConfluenceContentType.BLOG_POST,
				objectType,
			},
			skip: !recommendedPage || skip,
		},
	);

	const pageRecommendationData: PageRecommendationData = {
		data: undefined,
		loading: hoverPageLoading || recPageLoading,
		hoverPageError,
		recPageError,
	};

	if (
		hoverPageData?.singleContent?.history?.ownedBy?.displayName &&
		hoverPageData?.singleContent?.history?.ownedBy?.profilePicture?.path &&
		recommendedPage?.links?.webui &&
		recommendedPage?.title.text
	) {
		pageRecommendationData.data = {
			pageOwner: {
				name: hoverPageData.singleContent.history.ownedBy.displayName,
				avatarUrl: hoverPageData.singleContent.history.ownedBy.profilePicture.path,
			},
			page: {
				title: recommendedPage.title.text,
				url: `/wiki${recommendedPage.links.webui}`,
			},
			aiSnippet: AISnippetData?.latestKnowledgeGraphObject?.objectData,
		};
	}

	return pageRecommendationData;
};

/**
 * The ordering of recs array is determined by the "ranking" in the backend.
 * Each rec will have a corresponding "strategies" array that can have one or multiple strategies.
 * We want to ensure that we are choosing strategies from each rec in a way that avoids monotony on the UI.
 * To accomplish this, we must first sort the recs array depending on the number of strategies each rec has, in
 * ascending order. We then pass each rec to transformEndOfPageRecContent which helps to choose the strategy, and
 * finally we return the transformed array back to the intial ranking order determind by the backend
 */
const transformAndOrderRecs = (recs: any[] | undefined) => {
	if (!recs) return;

	const strategiesCounter = new Map();
	const recsSortedByStrategyCount = [...recs].sort((a, b) =>
		a.strategy.length > b.strategy.length ? 1 : -1,
	);
	const transformedRecs = recsSortedByStrategyCount.map((rec) =>
		transformEndOfPageRecContent(rec, strategiesCounter),
	);

	// return to initial order
	const pageCards: GlobalPageCard[] = new Array();
	recs.forEach((rec) => {
		const el = transformedRecs.find((card) => card.id === rec.content.id) as GlobalPageCard;
		pageCards.push(el);
	});
	return pageCards;
};
